import { getUserPreferencesFromContext } from '@urc-ui/utils/app-utils';
import { DEFAULT_COLUMN_STATE, DEFAULT_FILTER_STATE, DEFAULT_PAGE_SIZE } from '@urc-ui/store/constants';
import { ColumnState } from '@ag-grid-community/core';
import { DEFAULT_SORT_CONFIG } from '@urc-ui/store/api/subscriptions/subscriptions';
import { Sort } from '@urc-ui/store/api/types';
import { determineColumnStateFromSort } from '@urc-ui/utils/column-sorting-utils';

export const defaultUserPreferences = {
  subTablePageSize: { value: DEFAULT_PAGE_SIZE },
  subTableReportNameSearch: { value: '' },
  subTableColumns: { value: DEFAULT_COLUMN_STATE },
  subTableSort: { value: DEFAULT_SORT_CONFIG },
  subTableFilters: { value: DEFAULT_FILTER_STATE }
};

/**
 * Iterates through list of ColumnState to collect and return all column Ids of columns that are set to be visible.
 *
 * @param columns the current state of columns of the data table grid.
 */
export const findVisibleColumns = (columns: ColumnState[]) => {
  const visibleColumns: string[] = [];
  columns.forEach(column => {
    if (column.hide === false) {
      visibleColumns.push(column.colId);
    }
  });
  return visibleColumns;
};

/**
 * returns an updated data table's ColumnState[] where any ColumnState matching the saved column Ids are set to be visible.
 *
 * @param initialColumnStates current state of columns of the data table grid.
 * @param savedColumnIds list of column ids representing saved user preference for visible columns.
 */
export const applySavedVisibleColumns = (initialColumnStates: ColumnState[], savedColumnIds: string[]) => {
  const convertedColumns: ColumnState[] = [];
  initialColumnStates.forEach(column => {
    if (savedColumnIds.find(savedColumnId => savedColumnId === column.colId)) {
      column.hide = false;
      convertedColumns.push(column);
    } else {
      column.hide = true;
      convertedColumns.push(column);
    }
  });
  return convertedColumns;
};

/**
 * Applies the saved Column Sort option to the initial data table ColumnState[] and returns the updated list to be
 * applied to the data table.
 *
 * @param initialColumnStates current state of columns of the data table grid.
 * @param savedSortColumn the saved Sort object that is passed into a GetSubscriptionsRequest
 */
export const applySavedSortColumn = (initialColumnStates: ColumnState[], savedSortColumn: Sort) => {
  const convertedSortToColumnState = determineColumnStateFromSort(savedSortColumn);
  if (convertedSortToColumnState === null) {
    return initialColumnStates;
  }

  //This makes sure the sort value (ascending/descending) is consistent between saved and initial column state.
  const columnsWithAppliedSortPref: ColumnState[] = [];
  for (const column of initialColumnStates) {
    if (column.colId === convertedSortToColumnState.colId) {
      column.sort = convertedSortToColumnState.sort;
      columnsWithAppliedSortPref.push(column);
    } else {
      columnsWithAppliedSortPref.push(column);
    }
  }
  return columnsWithAppliedSortPref;
};

const mapUserPreferences = (allPreferences: Record<string, unknown>) => Object.assign(defaultUserPreferences, allPreferences);

export const loadUserPreferences = () => {
  const appContextUserPreferences = getUserPreferencesFromContext();
  const userPreferences =
    Object.keys(appContextUserPreferences || {}).length === 0 ? defaultUserPreferences : mapUserPreferences(appContextUserPreferences);

  return userPreferences;
};
